import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "@hooks/dark-mode";
import KnowledgeBankLayout from "./KnowledgeBankLayout";
import {
  Book,
  Leaf,
  FileText,
  BarChart3,
  Clock,
  Car,
  Package,
  HelpCircle,
  ArrowRight,
  Sparkles
} from "lucide-react";

// Category icons mapping
const categoryIcons: Record<string, React.ElementType> = {
  gettingStarted: Book,
  sustainability: Leaf,
  compliance: FileText,
  analytics: BarChart3,
  timeRegistration: Clock,
  drivingAllowance: Car,
  producerResponsibility: Package,
  faq: HelpCircle
};

const categoryColors: Record<string, { light: string; dark: string }> = {
  gettingStarted: { light: "#3b82f6", dark: "#60a5fa" },
  sustainability: { light: "#10b981", dark: "#34d399" },
  compliance: { light: "#8b5cf6", dark: "#a78bfa" },
  analytics: { light: "#f59e0b", dark: "#fbbf24" },
  timeRegistration: { light: "#ec4899", dark: "#f472b6" },
  drivingAllowance: { light: "#06b6d4", dark: "#22d3ee" },
  producerResponsibility: { light: "#84cc16", dark: "#a3e635" },
  faq: { light: "#6366f1", dark: "#818cf8" }
};

const KnowledgeBankHome = () => {
  const { t } = useTranslation();
  const { dark } = useDarkMode();

  const titleColor = dark ? "#ffffff" : "#111827";
  const textColor = dark ? "#d1d5db" : "#4b5563";
  const accent = dark ? "#60a5fa" : "#3b82f6";
  const cardBg = dark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const borderColor = dark ? "#374151" : "#e5e7eb";

  const categories = [
    { id: "gettingStarted", articleCount: 3, firstArticle: "introduction" },
    { id: "sustainability", articleCount: 3, firstArticle: "co2Tracking" },
    { id: "compliance", articleCount: 3, firstArticle: "csrd" },
    { id: "timeRegistration", articleCount: 3, firstArticle: "setup" },
    { id: "drivingAllowance", articleCount: 3, firstArticle: "gpsTracking" },
    { id: "producerResponsibility", articleCount: 3, firstArticle: "eprOverview" },
    { id: "faq", articleCount: 3, firstArticle: "general" }
  ];

  const featuredArticles = [
    { category: "gettingStarted", article: "quickStart" },
    { category: "sustainability", article: "co2Tracking" },
    { category: "compliance", article: "csrd" }
  ];

  return (
    <KnowledgeBankLayout title={t("knowledgeBank.pageTitle")}>
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4"
            style={{ backgroundColor: `${accent}15`, color: accent }}
          >
            <Book className="w-3.5 h-3.5" />
            {t("knowledgeBank.badge")}
          </div>
          <h1
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ color: titleColor }}
          >
            {t("knowledgeBank.hero.title")}
          </h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: textColor }}>
            {t("knowledgeBank.hero.subtitle")}
          </p>
        </div>

        {/* Featured Articles */}
        <section className="mb-16">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-5 h-5" style={{ color: accent }} />
            <h2 className="text-xl font-semibold" style={{ color: titleColor }}>
              {t("knowledgeBank.featured.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {featuredArticles.map(({ category, article }) => {
              const color = categoryColors[category]?.[dark ? "dark" : "light"] || accent;
              const Icon = categoryIcons[category] || Book;
              return (
                <Link
                  key={`${category}-${article}`}
                  to={`/knowledge-bank/${category}/${article}`}
                  className="group p-5 rounded-xl border transition-all hover:shadow-lg"
                  style={{
                    backgroundColor: cardBg,
                    borderColor
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${color}15` }}
                  >
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <p className="text-xs font-medium mb-1" style={{ color }}>
                    {t(`knowledgeBank.categories.${category}.title`)}
                  </p>
                  <h3 className="font-semibold mb-1 group-hover:text-blue-500 transition-colors" style={{ color: titleColor }}>
                    {t(`knowledgeBank.categories.${category}.articles.${article}`)}
                  </h3>
                  <p className="text-sm line-clamp-2" style={{ color: textColor }}>
                    {t(`knowledgeBank.categories.${category}.descriptions.${article}`)}
                  </p>
                </Link>
              );
            })}
          </div>
        </section>

        {/* All Categories */}
        <section>
          <h2 className="text-xl font-semibold mb-6" style={{ color: titleColor }}>
            {t("knowledgeBank.allCategories")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categories.map(({ id, articleCount, firstArticle }) => {
              const color = categoryColors[id]?.[dark ? "dark" : "light"] || accent;
              const Icon = categoryIcons[id] || Book;
              return (
                <Link
                  key={id}
                  to={`/knowledge-bank/${id}/${firstArticle}`}
                  className="group flex items-start gap-4 p-5 rounded-xl border transition-all hover:shadow-lg"
                  style={{
                    backgroundColor: cardBg,
                    borderColor
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${color}15` }}
                  >
                    <Icon className="w-6 h-6" style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold mb-1 group-hover:text-blue-500 transition-colors" style={{ color: titleColor }}>
                      {t(`knowledgeBank.categories.${id}.title`)}
                    </h3>
                    <p className="text-sm mb-2 line-clamp-2" style={{ color: textColor }}>
                      {t(`knowledgeBank.categories.${id}.description`)}
                    </p>
                    <span className="text-xs" style={{ color: textColor }}>
                      {articleCount} {t("knowledgeBank.articles")}
                    </span>
                  </div>
                  <ArrowRight
                    className="w-5 h-5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ color }}
                  />
                </Link>
              );
            })}
          </div>
        </section>

        {/* Help Section */}
        <section
          className="mt-16 p-8 rounded-2xl text-center"
          style={{
            background: dark
              ? "linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(139,92,246,0.05) 100%)"
              : "linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(139,92,246,0.04) 100%)"
          }}
        >
          <h2 className="text-2xl font-bold mb-2" style={{ color: titleColor }}>
            {t("knowledgeBank.help.title")}
          </h2>
          <p className="mb-6" style={{ color: textColor }}>
            {t("knowledgeBank.help.description")}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all hover:opacity-90"
            style={{ backgroundColor: accent, color: "#ffffff" }}
          >
            {t("knowledgeBank.help.cta")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </div>
    </KnowledgeBankLayout>
  );
};

export default KnowledgeBankHome;
